import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { staticUrl } from "services/data/mainUrl";
import styles from "./Apps.module.css";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Button, Card, CardActions, CardContent, Stack, Tooltip, Typography } from "@mui/material";
import { useGetDeviceAppsQuery, useUpdateDeviceAppsMutation } from "services/data/deviceApi";
import { useParams } from "react-router-dom";
import { DialogActionItem } from "components/grid";
import { AccessTime, Done, HideSource, SaveOutlined } from "@mui/icons-material";
import { isEqual } from "lodash";
import { HidingError } from "components/errors";
import StrippedDataGrid from "components/styled";
import { isoToDateTimeString } from "utils/date";

const appNameRenderer = ({value}) => {
  return ( <div className={styles.appName}>
            <div className={styles.title}>{value.name}</div>
            <div className={styles.package}>{value.package}</div>
          </div>
  );
};

const versionRenderer = ({value}) => {
  return (
    <div className={styles.versionWrapper}>
      <div className={styles.version}><span className={styles.versionContent}>{value.version}</span></div>
      <div className={styles.build}>сборка {value.build}</div>
    </div>
  )
};

const DEFAULT_ERROR = "Ошибка соединения с сервером";

const Apps = () => {

  const { id } = useParams();
  const { data, error } = useGetDeviceAppsQuery({id});
  const [updateDeviceApps, {error: updateDeviceAppsError, isLoading: isUpdatingDeviceApps, reset: updateDeviceAppsReset}] = useUpdateDeviceAppsMutation();
  const [items, setItems] = useState(data?.body?.items);

  useEffect(() => {
    setItems(data?.body?.items);
  }, [data]);

  const setDisabled = useCallback( (row) => {
    const index = items.findIndex(el => el.id === row.id);
    if(index !== -1) {
      setItems( (old) => [...old.slice(0, index), {...row, disabled: !row.disabled}, ...old.slice(index + 1)]);
    }
  }, [data, items]);

  const isSaveButtonDisabled = useCallback( () => isEqual(data?.body?.items, items), [data, items]);
  
  const hideError = () => {
    if(updateDeviceAppsError) updateDeviceAppsReset();
  };
  
  const openError = () => { 
    return !!updateDeviceAppsError || false;
  };

  const getErrorMessage = () => {
    if(updateDeviceAppsError) return updateDeviceAppsError?.data?.message ?? DEFAULT_ERROR;
  };

  const setupAt = useCallback( (value) => {
    return isoToDateTimeString(value.setupAt);  
  }, []);

  const updateAt = useCallback( (value) => {
    if(value.updateAt) {
      return isoToDateTimeString(value.updateAt); 
    }
    return 'не обновлялось';
  }, []);

  const execAt = useCallback( (value) => {
    if(value.execAt) {
      return isoToDateTimeString(value.execAt);
    }
    return 'не запускалось';
  }, []);
  
  const renderDate = ({value}) => { 
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{width: '100%'}}>
        <AccessTime fontSize="small" color='secondary'/>
        <Stack sx={{width: '100%'}}>
          <Tooltip title="Дата установки приложения">
            <Typography variant="caption" sx={{lineHeight: "0.8rem"}}>Установлено <span style={{float:"right"}}>{setupAt(value)}</span></Typography>
          </Tooltip>
          <Tooltip title="Дата последнего обновления приложения">
            <Typography variant="caption" sx={{lineHeight: "0.8rem"}}>Обновлено <span style={{float:"right"}}>{updateAt(value)}</span></Typography>
          </Tooltip> 
          <Tooltip title="Дата последнего запуска приложения">
            <Typography variant="caption" sx={{lineHeight: "0.8rem"}}>Запуск <span style={{float:"right"}}>{execAt(value)}</span></Typography>
          </Tooltip> 

        </Stack>
        
      </Stack>
    );
  };

  const rowClassName = (row, indexRelativeToCurrentPage) => {
    let result = indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
    row.disabled && (result += ' disabled');
    return result;
  };

  const columns = useMemo( () => [
      {field: 'icon', width: 50, display: 'flex', align: 'center', sortable: false, 
        renderCell: (params) => <img className={styles.icon} src={`${staticUrl}${params.row.icon}`} />},
      {field: 'app', flex: 1, valueGetter: (value, row) => ({name: row.name, package: row.package}), renderCell: appNameRenderer, display: 'flex', width: 150},
      {field: 'date', display: 'flex', renderCell: renderDate, valueGetter: (value, row) => (row), width: 240},
      {field: 'version', width: 150, align: 'right', valueGetter: (value, row) => (row), renderCell: versionRenderer},
      {field: 'actions', type: 'actions', align: "right", width: 50, getActions: ({row}) => [
        <DialogActionItem
          icon={ row.disabled ? <Done /> : <HideSource />}
          label={ row.disabled ? "Разблокировать" : "Заблокировать" }
          handler={()=>setDisabled(row)}
          title={row.disabled ? 'Разблокировать приложение': 'Заблокировать приложение'}
        >
          <span>{row.disabled ? 'Разблокировать' : 'Заблокировать'} приложение <b>{row.name}</b> версии <b>{row.version}</b> в кассе <b>{id}</b>?</span>                  
        </DialogActionItem>
      ]}  
    ], [items]
  );

  return (
    <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
      <HidingError 
          open={ openError() }
          message={ getErrorMessage() }
          onClose={hideError}
        /> 
      <CardContent sx={{overflow: 'auto'}}>
        <StrippedDataGrid
          getRowId={(row) => row.package}
          getRowClassName={({row, indexRelativeToCurrentPage}) => rowClassName(row, indexRelativeToCurrentPage)}
          rows={items}
          localeText={{noRowsLabel: error?.data?.message ?? "Приложений пока нет"}}
          columns={columns}
          columnHeaderHeight={1}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          disableMultipleRowSelection
          hideFooter
          sx={{
            width: '100%',
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
              outline: 'none',
            },
            [`& .${gridClasses.columnHeader}, & .MuiInputBase-root`]: {
              overflowX: 'unset'
            },
            [`& .${gridClasses.cell}.${gridClasses["cell--editing"]}:focus, & .${gridClasses.cell}.${gridClasses["cell--editing"]}:focus-within`]: {
              outline: 'none'
            }
          }}
        />
      </CardContent>
      <CardActions sx={{px: 2}}>
        <Button
          fullWidth
          disabled={isSaveButtonDisabled()}
          startIcon={<SaveOutlined/>}
          color='error'
          variant='contained'
          sx={{my: 1}}
          onClick={()=>updateDeviceApps({devId: id, body: items})}
        >Сохранить</Button>    
      </CardActions>
    </Card>
      
  );
};

export default Apps;